import { ArrowStatefulIcon } from '@mm/icons';
import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useViewActions } from '@mm/state/actions/viewActions';
import { connectedPageSelector } from '@mm/state/selectors/viewSelectors';
import { useActor } from '@xstate/react';
import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useContextSelector } from 'use-context-selector';
import Spinner from '../../../../../common/components/Spinner';
import { ContactDeleteModal } from '../../../contacts/components/ContactDeleteModal/ContactDeleteModal';
import ContactsDetailsHeader from '../../../contacts/components/ContactsDetails/ContactDetailsHeader/ContactDetailsHeader';
import { ChatVariablesTab } from '../../../contacts/components/ContactsDetails/ContactDetailsTabs/ChatVariablesTab/ChatVariablesTab';
import { ChatVariablesTags } from '../../../contacts/components/ContactsDetails/ContactDetailsTabs/chatVariablesTags/chatVariablesTags';
import { ContactContext, EmptySelectedContact } from '../../contactMachine';
import { LiveChatContext } from '../../LiveChatContext';
import { Notes } from './components/Notes';
import Sessions from './components/Sessions';
import './Contact.pcss';

type ContactProps = {
  collapsedContactView: boolean;
  setCollapsedContactView: (collapsed: boolean) => void;
};
export const Contact = ({
  collapsedContactView,
  setCollapsedContactView,
}: ContactProps) => {
  const { deleteSelectedContact } = useContactsCRUDActions();
  const connectedPage = useSelector(connectedPageSelector);

  const [deleteModalOpened, setDeleteModalOpened] = useState(false);

  const { showSlideoutMenu } = useViewActions();

  const lcService = useContextSelector(
    LiveChatContext,
    v => v.lcServiceRef.current!,
  );

  const [lcMachineState, lcMachineSend] = useActor(lcService);

  const { remoteUserId } = lcMachineState.context || {};

  const selectedContact =
    (lcMachineState.context.remoteUserId
      ? (
          lcMachineState.context.contactsRefs[
            lcMachineState.context.remoteUserId
          ]?.state.context as ContactContext
        )?.model
      : undefined) || EmptySelectedContact;

  const selectedContactMachine = remoteUserId
    ? lcMachineState.context.contactsRefs[remoteUserId]
    : undefined;

  const refreshSelectedContact = useCallback(() => {
    selectedContactMachine?.send({
      type: 'LC_CONTACT_REFRESH',
      payload: remoteUserId || '',
    });
  }, [selectedContactMachine?.id, remoteUserId]);

  if (!selectedContact) return null;

  const displayVariables = selectedContact.variables.filter(
    v => v.variable.name !== 'fb messenger user id',
  );

  const isBusinessContact = !!displayVariables.find(
    ({ variable }) => variable.name.toLocaleLowerCase() === 'business_contact',
  );

  const className = classNames('live-chat--contact', {
    'live-chat--contact--collapsed': collapsedContactView,
  });

  const updateRemoteUserId = (id: string | undefined) => {
    lcMachineSend({ type: 'LC_REMOTE_USER_ID_CHANGE', payload: id });
  };

  const deleteContact = async (eraseRemoteMessages: boolean) => {
    if (connectedPage && selectedContact.id) {
      await deleteSelectedContact(
        connectedPage.id,
        selectedContact.id,
        undefined,
        undefined,
        undefined,
        eraseRemoteMessages,
      );
      if (selectedContact.conversation) {
        lcMachineSend({
          type: 'LC_CONVERSATIONS_DELETE',
          payload: [selectedContact.remoteId],
        });
      }
      updateRemoteUserId(undefined);
    }
  };
  const onHeaderButtonClick = () => {
    showSlideoutMenu('attributesAndTags');
  };

  const showContactDetailsSlideout = () => {
    showSlideoutMenu('contact', {
      contactId: selectedContact.id,
      selectedContact,
      deleteSelectedContact: () => setDeleteModalOpened(true),
      withSessions: true,
    });
  };

  return selectedContact.id || remoteUserId ? (
    <div className={className}>
      <div className="contact-details-header__expand-contact-view-icon">
        <ArrowStatefulIcon
          color="white"
          size={17}
          direction={collapsedContactView ? 'left' : 'right'}
          onClick={() => showContactDetailsSlideout()}
        />
      </div>

      <ContactsDetailsHeader
        selectedContact={selectedContact}
        onChangeContactInfo={refreshSelectedContact}
        isLiveChatContacts
        deleteSelectedContact={() => setDeleteModalOpened(true)}
        collapsedContactView={collapsedContactView}
        isBusinessContact={isBusinessContact}
      />
      {selectedContact.loading ? (
        <Spinner
          size={24}
          show
          position="static"
          backgroundColor="transparent"
        />
      ) : (
        <div className="contact-details__blocks">
          <ChatVariablesTags
            contactId={selectedContact.id}
            tags={selectedContact.tags}
            selectedContact={selectedContact}
            onChange={refreshSelectedContact}
            isLiveChatContacts
            collapsedContactView={collapsedContactView}
          />

          <Sessions collapsedContactView={collapsedContactView} withSessions />
          <Notes
            contactId={selectedContact.id}
            notes={selectedContact.notes}
            selectedContact={selectedContact}
            onChange={refreshSelectedContact}
            collapsedContactView={collapsedContactView}
          />

          <ChatVariablesTab
            contactId={selectedContact.id}
            selectedContact={selectedContact}
            remoteUserId={selectedContact.remoteId}
            variables={displayVariables}
            onAttributeUpdated={refreshSelectedContact}
            isLiveChatContacts
            collapsedContactView={collapsedContactView}
            withHeaderButton
            withHeaderButtonText="Manage Attributes"
            headerButtonCss={{
              container: 'contact-details-header__manage-attributes-button',
            }}
            openArrowClassName="contact-details-header__arrow-icon-open"
            onHeaderButtonClick={onHeaderButtonClick}
            isBusinessContact={isBusinessContact}
          />

          <ContactDeleteModal
            showEraseRemoteMessagesOption={selectedContact.remoteId.startsWith(
              'em_',
            )}
            show={deleteModalOpened}
            emailAddress={selectedContact.email}
            onClose={() => setDeleteModalOpened(false)}
            onDelete={deleteContact}
          />
        </div>
      )}
    </div>
  ) : null;
};
